import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * DemoModeBanner
 * Only renders when:
 *   1. localStorage.isDemo === 'true'  AND
 *   2. the logged-in user is actually demo@lapistics.com.au
 *
 * Language is intentionally practical and reassuring:
 *   - "sample club" so they understand it's not a real club's data
 *   - "feel free to try anything" removes the fear of breaking something
 *   - "resets overnight" is friendlier than "resets daily at 3am"
 *   - CTA nudges toward signup without being pushy
 */
export default function DemoModeBanner() {
  const navigate = useNavigate();
  const { user }  = useAuth();

  const isDemo =
    localStorage.getItem('isDemo') === 'true' &&
    user?.email === 'demo@lapistics.com.au';

  if (!isDemo) return null;

  return (
    <Box sx={{
      position : 'fixed',
      top      : 0,
      left     : 0,
      right    : 0,
      zIndex   : 1099,           // below MUI AppBar (1100) so navbar stays on top
      background: 'linear-gradient(90deg, #012F3F 0%, #00525A 100%)',
      borderBottom: '2px solid #E8C547',
      px: { xs: 2, sm: 3 },
      py: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 1,
      minHeight: 40,
    }}>

      {/* Left: badge + message */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
        <Chip
          label="DEMO"
          size="small"
          sx={{
            background: '#E8C547', color: '#012F3F',
            fontWeight: 800, fontSize: '.66rem', letterSpacing: '.12em',
            height: 20, flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{
          color: 'rgba(255,248,237,.75)', fontSize: '.82rem', lineHeight: 1.4,
        }}>
          You're exploring a{' '}
          <strong style={{ color: 'rgba(255,248,237,.95)' }}>sample club</strong>
          {' '}— feel free to try anything.{' '}
          <span style={{ color: 'rgba(255,248,237,.55)' }}>
            Changes don't affect any real club and everything resets overnight.
          </span>
        </Typography>
      </Box>

      {/* Right: actions */}
      <Box sx={{ display: 'flex', gap: 1, py: 0.75, flexShrink: 0 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{
            color: 'rgba(255,248,237,.7)', borderColor: 'rgba(255,248,237,.2)',
            fontSize: '.74rem', fontWeight: 600, py: .3, px: 1.25, borderRadius: 100,
            '&:hover': { borderColor: '#E8C547', color: '#E8C547', background: 'transparent' },
          }}>
          ← Back to site
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            localStorage.removeItem('isDemo');
            navigate('/get-started');
          }}
          sx={{
            background: '#E8C547', color: '#012F3F',
            fontSize: '.74rem', fontWeight: 800, py: .3, px: 1.25, borderRadius: 100,
            boxShadow: 'none',
            '&:hover': { background: '#C9A420', boxShadow: 'none' },
          }}>
          Set up my club →
        </Button>
      </Box>
    </Box>
  );
}
